import { supabase } from '../supabaseClient'

const COLUNAS_PADRAO = [
  { slug: 'novo-lead', titulo: 'Novo Lead', ordem: 0, fixada: false, is_contrato_fechado: false },
  { slug: 'em-atendimento', titulo: 'Em Atendimento', ordem: 1, fixada: false, is_contrato_fechado: false },
  { slug: 'contrato-fechado', titulo: 'Contrato Fechado', ordem: 2, fixada: false, is_contrato_fechado: true }
]

function slugify(titulo) {
  return String(titulo || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || `quadro-${Date.now()}`
}

async function registrarHistorico({
  empresaId,
  leadId = null,
  colunaId = null,
  tipoAcao,
  colunaAnteriorId = null,
  colunaNovaId = null,
  detalhes = {},
  observacoes = null,
  usuarioId = null,
  nomeUsuario = null
}) {
  const { error } = await supabase.from('gestao_leads_historico').insert([{
    empresa_id: empresaId,
    lead_id: leadId,
    coluna_id: colunaId,
    tipo_acao: tipoAcao,
    coluna_anterior_id: colunaAnteriorId,
    coluna_nova_id: colunaNovaId,
    detalhes,
    observacoes,
    usuario_id: usuarioId,
    nome_usuario: nomeUsuario
  }])

  if (error) {
    console.error('Erro ao registrar histórico Gestão Leads:', error)
  }
}

/**
 * Garante colunas padrão da empresa.
 * Considera também inativas — para não recriar um quadro padrão que foi inativado.
 */
async function garantirColunasPadrao(empresaId, usuarioId = null) {
  const { data: existentes, error } = await supabase
    .from('gestao_leads_colunas')
    .select('id, slug, ativo')
    .eq('empresa_id', empresaId)
    .in('slug', COLUNAS_PADRAO.map((c) => c.slug))

  if (error) throw error

  const slugsOk = new Set((existentes || []).map((c) => c.slug))
  const faltando = COLUNAS_PADRAO.filter((c) => !slugsOk.has(c.slug))

  if (faltando.length > 0) {
    const { data: maxOrdem } = await supabase
      .from('gestao_leads_colunas')
      .select('ordem')
      .eq('empresa_id', empresaId)
      .order('ordem', { ascending: false })
      .limit(1)

    let ordemBase = (maxOrdem?.[0]?.ordem ?? -1) + 1
    const rows = faltando.map((c, i) => ({
      empresa_id: empresaId,
      slug: c.slug,
      titulo: c.titulo,
      ordem: ordemBase + i,
      fixada: c.fixada,
      is_contrato_fechado: c.is_contrato_fechado,
      ativo: true,
      visivel_empresa: true,
      created_by: usuarioId || null
    }))

    const { error: errInsert } = await supabase
      .from('gestao_leads_colunas')
      .insert(rows)

    if (errInsert) throw errInsert
  }
}

/**
 * Lista quadros que o usuário pode ver:
 * - privilegiado: todos da empresa
 * - criados por ele
 * - compartilhados com ele
 * - visivel_empresa sem nenhum compartilhamento: toda a empresa
 * - visivel_empresa com compartilhamentos: só dono + compartilhados (+ privilegiado)
 */
export async function listarColunasVisiveis({ empresaId, usuarioId, isPrivilegiado = false }) {
  await garantirColunasPadrao(empresaId, usuarioId)

  const { data: todas, error } = await supabase
    .from('gestao_leads_colunas')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('ativo', true)
    .order('ordem', { ascending: true })

  if (error) throw error

  if (isPrivilegiado) {
    return todas || []
  }

  const colunaIds = (todas || []).map((c) => c.id)
  if (colunaIds.length === 0) return []

  const { data: comps, error: errComp } = await supabase
    .from('gestao_leads_coluna_compartilhamentos')
    .select('coluna_id, usuario_id')
    .in('coluna_id', colunaIds)

  if (errComp) throw errComp

  const idsCompartilhadosComigo = new Set(
    (comps || []).filter((c) => c.usuario_id === usuarioId).map((c) => c.coluna_id)
  )
  const colunasComRestricao = new Set((comps || []).map((c) => c.coluna_id))

  return (todas || []).filter((col) => {
    if (col.created_by === usuarioId) return true
    if (idsCompartilhadosComigo.has(col.id)) return true
    // Quadro com lista de compartilhamento: só quem está na lista (além do dono/privilegiado)
    if (colunasComRestricao.has(col.id)) return false
    // Sem compartilhamentos: colunas padrão da empresa ficam abertas
    return col.visivel_empresa === true
  })
}

/** @deprecated use listarColunasVisiveis */
export async function garantirColunasEmpresa(empresaId, usuarioId = null, isPrivilegiado = false) {
  return listarColunasVisiveis({ empresaId, usuarioId, isPrivilegiado })
}

/**
 * Lista cards/leads visíveis:
 * - privilegiado + verTodos: todos da empresa (nas colunas informadas)
 * - demais: próprios (created_by / responsavel_id) + compartilhados com o usuário
 */
export async function listarLeads({
  empresaId,
  colunaIds = null,
  usuarioId = null,
  isPrivilegiado = false,
  verTodos = false,
  filtroUsuarioIds = null
}) {
  let query = supabase
    .from('gestao_leads')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('ativo', true)
    .order('ordem', { ascending: true })
    .order('created_at', { ascending: true })

  if (colunaIds && colunaIds.length > 0) {
    query = query.in('coluna_id', colunaIds)
  } else if (colunaIds && colunaIds.length === 0) {
    return []
  }

  const { data, error } = await query
  if (error) throw error

  let todos = data || []

  if (isPrivilegiado && verTodos) {
    if (filtroUsuarioIds && filtroUsuarioIds.length > 0) {
      const setIds = new Set(filtroUsuarioIds)
      todos = todos.filter((lead) =>
        setIds.has(lead.created_by) || setIds.has(lead.responsavel_id)
      )
    }
    return todos
  }

  if (!usuarioId) return []

  const { data: comps, error: errComp } = await supabase
    .from('gestao_leads_card_compartilhamentos')
    .select('lead_id')
    .eq('usuario_id', usuarioId)

  if (errComp) throw errComp

  const idsCompartilhados = new Set((comps || []).map((c) => c.lead_id))

  return todos.filter((lead) =>
    lead.created_by === usuarioId ||
    lead.responsavel_id === usuarioId ||
    idsCompartilhados.has(lead.id)
  )
}

export async function listarCompartilhamentosCard(leadId) {
  const { data, error } = await supabase
    .from('gestao_leads_card_compartilhamentos')
    .select('id, usuario_id, compartilhado_por, created_at')
    .eq('lead_id', leadId)

  if (error) throw error
  return data || []
}

export async function salvarCompartilhamentosCard({
  lead,
  usuarioIds,
  compartilhadoPor,
  nomeUsuario
}) {
  const { data: atuais, error: errList } = await supabase
    .from('gestao_leads_card_compartilhamentos')
    .select('id, usuario_id')
    .eq('lead_id', lead.id)

  if (errList) throw errList

  const donoId = lead.created_by || lead.responsavel_id
  const atuaisIds = new Set((atuais || []).map((a) => a.usuario_id))
  const novosIds = new Set(usuarioIds.filter((id) => id && id !== donoId))

  const remover = (atuais || []).filter((a) => !novosIds.has(a.usuario_id))
  const adicionar = [...novosIds].filter((id) => !atuaisIds.has(id))

  if (remover.length > 0) {
    const { error } = await supabase
      .from('gestao_leads_card_compartilhamentos')
      .delete()
      .in('id', remover.map((r) => r.id))
    if (error) throw error
  }

  if (adicionar.length > 0) {
    const { error } = await supabase
      .from('gestao_leads_card_compartilhamentos')
      .insert(adicionar.map((usuario_id) => ({
        lead_id: lead.id,
        usuario_id,
        compartilhado_por: compartilhadoPor || null
      })))
    if (error) throw error
  }

  await registrarHistorico({
    empresaId: lead.empresa_id,
    leadId: lead.id,
    colunaId: lead.coluna_id,
    tipoAcao: 'compartilhar_card',
    detalhes: {
      usuario_ids: [...novosIds],
      adicionados: adicionar,
      removidos: remover.map((r) => r.usuario_id)
    },
    observacoes: `Compartilhamento do lead "${lead.nome}" atualizado`,
    usuarioId: compartilhadoPor,
    nomeUsuario
  })

  return listarCompartilhamentosCard(lead.id)
}

export function podeGerenciarCompartilhamentoCard(lead, userId, isPrivilegiado) {
  if (!lead) return false
  if (isPrivilegiado) return true
  return lead.created_by === userId || lead.responsavel_id === userId
}

export async function listarUsuariosEmpresa(empresaId) {
  let query = supabase
    .from('usuarios_sistema')
    .select('id, nome, email, role')
    .eq('status', 'ativo')
    .order('nome')

  if (empresaId) {
    query = query.eq('empresa_id', empresaId)
  }

  const { data, error } = await query
  if (error) throw error
  return data || []
}

export async function listarCompartilhamentos(colunaId) {
  const { data, error } = await supabase
    .from('gestao_leads_coluna_compartilhamentos')
    .select('id, usuario_id, compartilhado_por, created_at')
    .eq('coluna_id', colunaId)

  if (error) throw error
  return data || []
}

/**
 * Substitui a lista de usuários com quem o quadro está compartilhado.
 * Só o dono (ou privilegiado) deve chamar — validação no frontend.
 */
export async function salvarCompartilhamentos({
  coluna,
  usuarioIds,
  compartilhadoPor,
  nomeUsuario
}) {
  const { data: atuais, error: errList } = await supabase
    .from('gestao_leads_coluna_compartilhamentos')
    .select('id, usuario_id')
    .eq('coluna_id', coluna.id)

  if (errList) throw errList

  const atuaisIds = new Set((atuais || []).map((a) => a.usuario_id))
  const novosIds = new Set(usuarioIds.filter((id) => id && id !== coluna.created_by))

  const remover = (atuais || []).filter((a) => !novosIds.has(a.usuario_id))
  const adicionar = [...novosIds].filter((id) => !atuaisIds.has(id))

  if (remover.length > 0) {
    const { error } = await supabase
      .from('gestao_leads_coluna_compartilhamentos')
      .delete()
      .in('id', remover.map((r) => r.id))
    if (error) throw error
  }

  if (adicionar.length > 0) {
    const { error } = await supabase
      .from('gestao_leads_coluna_compartilhamentos')
      .insert(adicionar.map((usuario_id) => ({
        coluna_id: coluna.id,
        usuario_id,
        compartilhado_por: compartilhadoPor || null
      })))
    if (error) throw error
  }

  await registrarHistorico({
    empresaId: coluna.empresa_id,
    colunaId: coluna.id,
    tipoAcao: 'compartilhar_coluna',
    detalhes: {
      usuario_ids: [...novosIds],
      adicionados: adicionar,
      removidos: remover.map((r) => r.usuario_id)
    },
    observacoes: `Compartilhamento do quadro "${coluna.titulo}" atualizado`,
    usuarioId: compartilhadoPor,
    nomeUsuario
  })

  return listarCompartilhamentos(coluna.id)
}

export async function criarLead({
  empresaId,
  colunaId,
  nome,
  telefone,
  observacoes,
  usuarioId,
  nomeUsuario
}) {
  const { data: maxOrdem } = await supabase
    .from('gestao_leads')
    .select('ordem')
    .eq('coluna_id', colunaId)
    .eq('ativo', true)
    .order('ordem', { ascending: false })
    .limit(1)

  const ordem = (maxOrdem?.[0]?.ordem ?? -1) + 1

  const { data, error } = await supabase
    .from('gestao_leads')
    .insert([{
      empresa_id: empresaId,
      coluna_id: colunaId,
      nome,
      telefone,
      observacoes: observacoes || null,
      ordem,
      responsavel_id: usuarioId || null,
      created_by: usuarioId || null
    }])
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId,
    leadId: data.id,
    colunaId,
    tipoAcao: 'criar',
    colunaNovaId: colunaId,
    detalhes: { nome, telefone },
    observacoes: 'Lead criado',
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function atualizarLead({
  lead,
  nome,
  telefone,
  observacoes,
  usuarioId,
  nomeUsuario
}) {
  const { data, error } = await supabase
    .from('gestao_leads')
    .update({
      nome,
      telefone,
      observacoes: observacoes || null
    })
    .eq('id', lead.id)
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId: lead.empresa_id,
    leadId: lead.id,
    colunaId: lead.coluna_id,
    tipoAcao: 'editar',
    detalhes: {
      antes: { nome: lead.nome, telefone: lead.telefone, observacoes: lead.observacoes },
      depois: { nome, telefone, observacoes }
    },
    observacoes: 'Lead editado',
    usuarioId,
    nomeUsuario
  })

  return data
}

/** Inativa o card (ativo=false). A listagem só retorna ativos. */
export async function inativarLead({ lead, usuarioId, nomeUsuario }) {
  const { error } = await supabase
    .from('gestao_leads')
    .update({ ativo: false })
    .eq('id', lead.id)

  if (error) throw error

  await registrarHistorico({
    empresaId: lead.empresa_id,
    leadId: lead.id,
    colunaId: lead.coluna_id,
    tipoAcao: 'inativar',
    detalhes: { nome: lead.nome, telefone: lead.telefone },
    observacoes: 'Lead inativado',
    usuarioId,
    nomeUsuario
  })
}

/** @deprecated use inativarLead */
export async function excluirLead(args) {
  return inativarLead(args)
}

export async function moverLead({
  lead,
  colunaNovaId,
  novaOrdem = null,
  usuarioId,
  nomeUsuario,
  tituloColunaAnterior = null,
  tituloColunaNova = null
}) {
  if (lead.coluna_id === colunaNovaId && novaOrdem == null) {
    return lead
  }

  let ordem = novaOrdem
  if (ordem == null) {
    const { data: maxOrdem } = await supabase
      .from('gestao_leads')
      .select('ordem')
      .eq('coluna_id', colunaNovaId)
      .eq('ativo', true)
      .order('ordem', { ascending: false })
      .limit(1)
    ordem = (maxOrdem?.[0]?.ordem ?? -1) + 1
  }

  const { data, error } = await supabase
    .from('gestao_leads')
    .update({
      coluna_id: colunaNovaId,
      ordem
    })
    .eq('id', lead.id)
    .select('*')
    .single()

  if (error) throw error

  if (lead.coluna_id !== colunaNovaId) {
    await registrarHistorico({
      empresaId: lead.empresa_id,
      leadId: lead.id,
      colunaId: colunaNovaId,
      tipoAcao: 'mover',
      colunaAnteriorId: lead.coluna_id,
      colunaNovaId,
      detalhes: {
        de: tituloColunaAnterior,
        para: tituloColunaNova
      },
      observacoes: `Movido de "${tituloColunaAnterior || '?'}" para "${tituloColunaNova || '?'}"`,
      usuarioId,
      nomeUsuario
    })
  }

  return data
}

export async function efetivarContrato({
  lead,
  razaoSocial,
  documento,
  emailFinanceiro,
  valorContrato,
  usuarioId,
  nomeUsuario
}) {
  const { data, error } = await supabase
    .from('gestao_leads')
    .update({
      razao_social: razaoSocial,
      documento,
      email_financeiro: emailFinanceiro,
      valor_contrato: valorContrato,
      cliente_efetivado: true,
      efetivado_em: new Date().toISOString()
    })
    .eq('id', lead.id)
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId: lead.empresa_id,
    leadId: lead.id,
    colunaId: lead.coluna_id,
    tipoAcao: 'cadastrar_contrato',
    detalhes: {
      razao_social: razaoSocial,
      documento,
      email_financeiro: emailFinanceiro,
      valor_contrato: valorContrato
    },
    observacoes: 'Cliente efetivado (contrato fechado)',
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function criarColuna({
  empresaId,
  titulo,
  usuarioId,
  nomeUsuario
}) {
  const { data: maxOrdem } = await supabase
    .from('gestao_leads_colunas')
    .select('ordem')
    .eq('empresa_id', empresaId)
    .eq('ativo', true)
    .order('ordem', { ascending: false })
    .limit(1)

  const ordem = (maxOrdem?.[0]?.ordem ?? -1) + 1
  let slug = slugify(titulo)

  const { data: conflito } = await supabase
    .from('gestao_leads_colunas')
    .select('id')
    .eq('empresa_id', empresaId)
    .eq('slug', slug)
    .maybeSingle()

  if (conflito) {
    slug = `${slug}-${Date.now().toString(36)}`
  }

  const { data, error } = await supabase
    .from('gestao_leads_colunas')
    .insert([{
      empresa_id: empresaId,
      slug,
      titulo,
      ordem,
      fixada: false,
      is_contrato_fechado: false,
      ativo: true,
      visivel_empresa: false,
      created_by: usuarioId || null
    }])
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId,
    colunaId: data.id,
    tipoAcao: 'criar_coluna',
    detalhes: { titulo, slug },
    observacoes: `Quadro "${titulo}" criado (privado — compartilhe para outros verem)`,
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function alternarFixacaoColuna({
  coluna,
  usuarioId,
  nomeUsuario
}) {
  const novaFixada = !coluna.fixada
  const { data, error } = await supabase
    .from('gestao_leads_colunas')
    .update({ fixada: novaFixada })
    .eq('id', coluna.id)
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId: coluna.empresa_id,
    colunaId: coluna.id,
    tipoAcao: 'fixar_coluna',
    detalhes: { fixada: novaFixada, titulo: coluna.titulo },
    observacoes: novaFixada
      ? `Quadro "${coluna.titulo}" fixado`
      : `Quadro "${coluna.titulo}" desfixado`,
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function renomearColuna({
  coluna,
  novoTitulo,
  usuarioId,
  nomeUsuario
}) {
  const titulo = String(novoTitulo || '').trim()
  if (!titulo) throw new Error('Título obrigatório')

  const { data, error } = await supabase
    .from('gestao_leads_colunas')
    .update({ titulo })
    .eq('id', coluna.id)
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId: coluna.empresa_id,
    colunaId: coluna.id,
    tipoAcao: 'renomear_coluna',
    detalhes: { antes: coluna.titulo, depois: titulo, slug: coluna.slug },
    observacoes: `Quadro renomeado de "${coluna.titulo}" para "${titulo}"`,
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function inativarColuna({
  coluna,
  usuarioId,
  nomeUsuario
}) {
  const { data, error } = await supabase
    .from('gestao_leads_colunas')
    .update({ ativo: false })
    .eq('id', coluna.id)
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId: coluna.empresa_id,
    colunaId: coluna.id,
    tipoAcao: 'inativar_coluna',
    detalhes: { titulo: coluna.titulo, slug: coluna.slug, visivel_empresa: coluna.visivel_empresa },
    observacoes: `Quadro "${coluna.titulo}" inativado`,
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function reativarColuna({
  coluna,
  usuarioId,
  nomeUsuario
}) {
  const { data, error } = await supabase
    .from('gestao_leads_colunas')
    .update({ ativo: true })
    .eq('id', coluna.id)
    .select('*')
    .single()

  if (error) throw error

  await registrarHistorico({
    empresaId: coluna.empresa_id,
    colunaId: coluna.id,
    tipoAcao: 'reativar_coluna',
    detalhes: { titulo: coluna.titulo, slug: coluna.slug },
    observacoes: `Quadro "${coluna.titulo}" reativado`,
    usuarioId,
    nomeUsuario
  })

  return data
}

export async function listarColunasInativas(empresaId) {
  const { data, error } = await supabase
    .from('gestao_leads_colunas')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('ativo', false)
    .order('titulo', { ascending: true })

  if (error) throw error
  return data || []
}

export function podeGerenciarColuna(coluna, userId, isPrivilegiado) {
  return podeGerenciarCompartilhamento(coluna, userId, isPrivilegiado)
}

export async function reordenarColunas({
  empresaId,
  colunaIdsOrdenados,
  usuarioId,
  nomeUsuario
}) {
  const updates = colunaIdsOrdenados.map((id, index) =>
    supabase
      .from('gestao_leads_colunas')
      .update({ ordem: index })
      .eq('id', id)
      .eq('empresa_id', empresaId)
  )

  const results = await Promise.all(updates)
  const failed = results.find((r) => r.error)
  if (failed?.error) throw failed.error

  await registrarHistorico({
    empresaId,
    tipoAcao: 'reordenar_coluna',
    detalhes: { ordem: colunaIdsOrdenados },
    observacoes: 'Quadros reordenados',
    usuarioId,
    nomeUsuario
  })
}

export async function listarHistoricoLead(leadId) {
  const { data, error } = await supabase
    .from('gestao_leads_historico')
    .select('*')
    .eq('lead_id', leadId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data || []
}

/**
 * Histórico de movimentações de cards (leads) da empresa, com filtros.
 */
export async function listarHistoricoMovimentacoes({
  empresaId,
  dataInicio,
  dataFim,
  usuarioId = null
}) {
  let query = supabase
    .from('gestao_leads_historico')
    .select('*')
    .eq('empresa_id', empresaId)
    .not('lead_id', 'is', null)
    .gte('created_at', `${dataInicio}T00:00:00`)
    .lte('created_at', `${dataFim}T23:59:59.999`)
    .order('created_at', { ascending: false })
    .limit(500)

  if (usuarioId) {
    query = query.eq('usuario_id', usuarioId)
  }

  const { data, error } = await query
  if (error) throw error

  const rows = data || []
  const leadIds = [...new Set(rows.map((r) => r.lead_id).filter(Boolean))]
  let leadsMap = {}

  if (leadIds.length > 0) {
    const { data: leadsData, error: errLeads } = await supabase
      .from('gestao_leads')
      .select('id, nome, telefone, created_by, responsavel_id')
      .in('id', leadIds)

    if (!errLeads && leadsData) {
      leadsMap = Object.fromEntries(leadsData.map((l) => [l.id, l]))
    }
  }

  return rows.map((r) => ({
    ...r,
    lead: leadsMap[r.lead_id] || null
  }))
}

export function podeGerenciarCompartilhamento(coluna, userId, isPrivilegiado) {
  if (!coluna) return false
  if (isPrivilegiado) return true
  return coluna.created_by === userId
}

export { slugify, COLUNAS_PADRAO }
